import React, { useEffect, useMemo, useState } from 'react';
import Map, { Layer, NavigationControl, Source } from 'react-map-gl';
import type { Feature, FeatureCollection, MultiPolygon, Point, Polygon } from 'geojson';
import 'mapbox-gl/dist/mapbox-gl.css';

interface PatternPointMarker {
    id: number;
    label?: string | null;
    pointIndex?: number | null;
    xLocal?: number | null;
    yLocal?: number | null;
    gpsLat?: number | null;
    gpsLon?: number | null;
}

interface PatternPointMapCardProps {
    areaName?: string;
    areaGeometry?: Polygon | MultiPolygon | null;
    patternCoordSystem?: string | null;
    patternPoints?: PatternPointMarker[];
    selectedPatternPointId?: number | null;
    measurementPoint?: Point | null;
    /** Initial map bearing in degrees (clockwise from north). Use to orient first pattern row W→E. */
    initialBearing?: number | null;
    /** Object/blob URL of the area-type template image to overlay on the map. */
    templateImageUrl?: string | null;
    /**
     * 4 GPS corners for the template image overlay, in Mapbox order:
     * [topLeft, topRight, bottomRight, bottomLeft] as [lng, lat].
     */
    templateCorners?: [[number, number], [number, number], [number, number], [number, number]] | null;
}

const MAPBOX_TOKEN =
    process.env.REACT_APP_MAPBOX_TOKEN ||
    'pk.eyJ1IjoiZS1uYW5vIiwiYSI6ImNtamM0cjZzcjBtMW8zZnNiZ2I4NmljYTIifQ.MP4bn1hVGwhOXujMC4wjUw';

const DEFAULT_VIEW = {
    longitude: -0.1278,
    latitude: 51.5074,
    zoom: 14,
    bearing: 0,
    pitch: 0,
};

interface Bounds {
    minLon: number;
    maxLon: number;
    minLat: number;
    maxLat: number;
    spanLon: number;
    spanLat: number;
    center: [number, number];
}

const flattenCoordinates = (geometry: Polygon | MultiPolygon): [number, number][] => {
    const collector: [number, number][] = [];
    if (geometry.type === 'Polygon') {
        geometry.coordinates.forEach((ring) => {
            ring.forEach((coord) => {
                if (coord.length >= 2) {
                    collector.push([coord[0], coord[1]]);
                }
            });
        });
        return collector;
    }

    geometry.coordinates.forEach((polygon) => {
        polygon.forEach((ring) => {
            ring.forEach((coord) => {
                if (coord.length >= 2) {
                    collector.push([coord[0], coord[1]]);
                }
            });
        });
    });
    return collector;
};

const computeBounds = (geometry?: Polygon | MultiPolygon | null): Bounds | null => {
    if (!geometry) return null;
    const coords = flattenCoordinates(geometry);
    if (!coords.length) return null;
    let minLon = coords[0][0];
    let maxLon = coords[0][0];
    let minLat = coords[0][1];
    let maxLat = coords[0][1];
    coords.forEach(([lon, lat]) => {
        minLon = Math.min(minLon, lon);
        maxLon = Math.max(maxLon, lon);
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
    });
    return {
        minLon,
        maxLon,
        minLat,
        maxLat,
        spanLon: maxLon - minLon,
        spanLat: maxLat - minLat,
        center: [(minLon + maxLon) / 2, (minLat + maxLat) / 2],
    };
};

const clamp01 = (value?: number | null) => {
    if (typeof value !== 'number') return 0;
    if (value < 0) return 0;
    if (value > 1) return 1;
    return value;
};

const resolvePointCoordinates = (
    point: PatternPointMarker,
    coordSystem: string | null | undefined,
    bounds: Bounds | null,
): [number, number] | null => {
    if (typeof point.gpsLon === 'number' && typeof point.gpsLat === 'number') {
        return [point.gpsLon, point.gpsLat];
    }
    if (coordSystem && coordSystem.toLowerCase() === 'gps') {
        return point.gpsLon != null && point.gpsLat != null ? [point.gpsLon, point.gpsLat] : null;
    }
    if (!bounds) return null;
    const x = clamp01(point.xLocal);
    const y = clamp01(point.yLocal);
    const lon = bounds.minLon + x * (bounds.maxLon - bounds.minLon);
    const lat = bounds.minLat + y * (bounds.maxLat - bounds.minLat);
    return [lon, lat];
};

const estimateZoom = (bounds: Bounds | null) => {
    if (!bounds) return DEFAULT_VIEW.zoom;
    const span = Math.max(bounds.spanLon, bounds.spanLat);
    if (span <= 0) {
        return 17;
    }
    // +2 extra zoom levels compared to area bounds so points fill the card
    const approx = 14 - Math.log2(span / 0.01);
    return Math.max(10, Math.min(20, approx));
};

/** Compute tight bounds from a set of GeoJSON point features (already resolved to GPS). */
const computePointsBounds = (features: Array<{ geometry: { coordinates: number[] } }>): Bounds | null => {
    if (!features.length) return null;
    let minLon = features[0].geometry.coordinates[0];
    let maxLon = minLon;
    let minLat = features[0].geometry.coordinates[1];
    let maxLat = minLat;
    features.forEach((f) => {
        const [lon, lat] = f.geometry.coordinates;
        if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
        minLon = Math.min(minLon, lon);
        maxLon = Math.max(maxLon, lon);
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
    });
    const spanLon = maxLon - minLon;
    const spanLat = maxLat - minLat;
    return {
        minLon, maxLon, minLat, maxLat, spanLon, spanLat,
        center: [(minLon + maxLon) / 2, (minLat + maxLat) / 2],
    };
};

const PatternPointMapCard: React.FC<PatternPointMapCardProps> = ({
    areaName,
    areaGeometry,
    patternCoordSystem,
    patternPoints = [],
    selectedPatternPointId,
    measurementPoint,
    initialBearing,
    templateImageUrl,
    templateCorners,
}) => {
    const areaFeature = useMemo<Feature<Polygon | MultiPolygon> | null>(() => {
        if (!areaGeometry) return null;
        return {
            type: 'Feature',
            geometry: areaGeometry,
            properties: { name: areaName || 'Area' },
        };
    }, [areaGeometry, areaName]);

    const bounds = useMemo(() => computeBounds(areaGeometry || null), [areaGeometry]);

    const pointFeatures = useMemo<Feature<Point>[]>(() => {
        if (!patternPoints.length) return [];
        return patternPoints
            .map((point) => {
                const coords = resolvePointCoordinates(point, patternCoordSystem, bounds);
                if (!coords) return null;
                return {
                    type: 'Feature',
                    geometry: { type: 'Point', coordinates: coords },
                    properties: {
                        id: point.id,
                        label: point.label ?? String(point.pointIndex ?? point.id),
                        pointIndex: point.pointIndex ?? point.id,
                        isSelected: point.id === selectedPatternPointId,
                    },
                };
            })
            .filter(Boolean) as Feature<Point>[];
    }, [patternPoints, patternCoordSystem, bounds, selectedPatternPointId]);

    // Prefer tight bounds from actual pattern point locations over area polygon bounds
    const displayBounds = useMemo(
        () => computePointsBounds(pointFeatures as any) || bounds,
        [pointFeatures, bounds]
    );

    const [viewState, setViewState] = useState(() => ({
        ...DEFAULT_VIEW,
        bearing: initialBearing ?? 0,
        ...(displayBounds
            ? {
                  longitude: displayBounds.center[0],
                  latitude: displayBounds.center[1],
                  zoom: estimateZoom(displayBounds),
              }
            : {}),
    }));

    useEffect(() => {
        if (displayBounds) {
            setViewState((prev) => ({
                ...prev,
                longitude: displayBounds.center[0],
                latitude: displayBounds.center[1],
                zoom: estimateZoom(displayBounds),
                bearing: initialBearing ?? prev.bearing,
            }));
        }
    }, [displayBounds, initialBearing]);

    if (!areaFeature) {
        return (
            <div className="pattern-map-fallback">
                <p>Select an area to preview its geometry.</p>
            </div>
        );
    }

    const pointCollection: FeatureCollection<Point> = {
        type: 'FeatureCollection',
        features: pointFeatures,
    };

    return (
        <div className="pattern-map-wrapper">
            <Map
                mapboxAccessToken={MAPBOX_TOKEN}
                mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
                {...viewState}
                onMove={(evt) => setViewState(evt.viewState)}
                style={{ width: '100%', height: '100%' }}
            >
                {/* Compass rose — rotates to show current map bearing vs. geographic north */}
                <NavigationControl
                    position="top-left"
                    showCompass
                    showZoom={false}
                />

                {/* Template image ground overlay — rendered first (lowest z-order) */}
                {templateImageUrl && templateCorners ? (
                    <Source
                        id="template-image"
                        type="image"
                        url={templateImageUrl}
                        coordinates={templateCorners}
                    >
                        <Layer
                            id="template-image-layer"
                            type="raster"
                            paint={{
                                'raster-opacity': 0.65,
                                'raster-fade-duration': 0,
                            }}
                        />
                    </Source>
                ) : null}

                {/* Area polygon outline */}
                <Source id="area-geometry" type="geojson" data={areaFeature}>
                    <Layer
                        id="area-fill"
                        type="fill"
                        paint={{
                            'fill-color': '#2f54eb',
                            'fill-opacity': templateImageUrl ? 0.08 : 0.2,
                        }}
                    />
                    <Layer
                        id="area-outline"
                        type="line"
                        paint={{
                            'line-color': '#1d39c4',
                            'line-width': 2,
                        }}
                    />
                </Source>

                {/* Pattern point circles + sequence numbers */}
                {pointFeatures.length > 0 && (
                    <Source id="pattern-points" type="geojson" data={pointCollection}>
                        {/* Circle base */}
                        <Layer
                            id="pattern-points-layer"
                            type="circle"
                            paint={{
                                'circle-radius': [
                                    'case',
                                    ['==', ['get', 'id'], selectedPatternPointId ?? -1],
                                    13,
                                    10,
                                ],
                                'circle-color': [
                                    'case',
                                    ['==', ['get', 'id'], selectedPatternPointId ?? -1],
                                    '#f5222d',
                                    '#fa8c16',
                                ],
                                'circle-stroke-color': '#ffffff',
                                'circle-stroke-width': 2,
                            }}
                        />
                        {/* Point index labels */}
                        <Layer
                            id="pattern-points-labels"
                            type="symbol"
                            layout={{
                                'text-field': ['to-string', ['get', 'pointIndex']],
                                'text-size': 11,
                                'text-anchor': 'center',
                                'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
                            }}
                            paint={{
                                'text-color': '#ffffff',
                            }}
                        />
                    </Source>
                )}
            </Map>
        </div>
    );
};

export default PatternPointMapCard;
